import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Checkbox, Button, Alert, Spin, Tag, Space, message, Empty, Typography } from 'antd';
import { MailOutlined, ThunderboltOutlined, DollarOutlined, CalendarOutlined, EnvironmentOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title, Text } = Typography;
const BRAND = '#25D366';

// Modal pra criar nova convocação PRA UM JOGO ESPECÍFICO.
// Recebe { match: { source, id, title, date, time, quadra, city } } — já
// cliente do jogo, então título/data/hora não são digitados. Só seleciona
// quem convoca + confirma.
//
// Canal-aware: filtra roster por elegibilidade
//   - email: precisa cp.email
//   - wa (futuro): todos passam
//   - telegram (futuro): só quem tem chat_id
export default function NewConvocationModal({ open, teamId, teamName, match, onClose, onCreated }) {
  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const channel = 'email';

  const channelFilter = (p) => {
    if (channel === 'email') return !!p.email;
    if (channel === 'wa') return !!p.phone;
    if (channel === 'telegram') return !!p.telegram_chat_id;
    return true;
  };

  useEffect(() => {
    if (!open || !teamId) return;
    setLoadingRoster(true);
    api.get(`/community/teams/${teamId}/roster`)
      .then(r => {
        const items = (r.data?.items || []).filter(p => p.cp_id);
        setRoster(items);
        setSelectedIds(items.filter(channelFilter).map(p => p.cp_id));
      })
      .catch(() => message.error('Erro ao carregar elenco'))
      .finally(() => setLoadingRoster(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, teamId]);

  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setQuote(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || selectedIds.length === 0) { setQuote(null); return; }
    setLoadingQuote(true);
    const t = setTimeout(() => {
      api.get('/community/convocations/quote/preview', {
        params: { team_id: teamId, channel, n: selectedIds.length },
      })
        .then(r => setQuote(r.data))
        .catch(() => setQuote(null))
        .finally(() => setLoadingQuote(false));
    }, 200);
    return () => clearTimeout(t);
  }, [open, teamId, selectedIds]);

  const availableRoster = useMemo(() => roster.filter(channelFilter), [roster]);
  const excludedCount = roster.length - availableRoster.length;
  const excludedReason = channel === 'email' ? 'sem email cadastrado'
                       : channel === 'telegram' ? 'sem Telegram vinculado'
                       : 'inelegível';

  const toggleAll = (list, checked) => {
    const ids = list.map(p => p.cp_id);
    if (checked) setSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
    else setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
  };

  const submit = async () => {
    if (!match) return message.error('Jogo não selecionado');
    if (selectedIds.length === 0) return message.warning('Selecione pelo menos 1 jogador');
    if (!quote) return message.warning('Aguarde cálculo do custo');
    if (quote.credits_cost > quote.credits_available) {
      return message.error(`Saldo insuficiente. Precisa ${quote.credits_cost} · tem ${quote.credits_available}`);
    }
    setSubmitting(true);
    try {
      const r = await api.post('/community/convocations', {
        team_id: teamId,
        match_source: match.source,
        match_id: match.id,
        channel,
        recipient_cp_ids: selectedIds,
      });
      message.success(`Convocação enviada pra ${r.data.total_recipients} jogadores`);
      onCreated?.(r.data);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Erro ao enviar';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const matchDate = match?.date ? dayjs(match.date).format('dddd, DD/MM/YYYY') : '—';
  const matchTime = match?.time ? String(match.time).slice(0, 5) : '';

  return (
    <Modal
      title={<><ThunderboltOutlined style={{ color: BRAND }} /> Nova convocação</>}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okButtonProps={{
        loading: submitting,
        disabled: selectedIds.length === 0 || !quote || quote.credits_cost > quote.credits_available,
        style: { background: BRAND, borderColor: BRAND },
      }}
      okText={quote ? `Enviar (${quote.credits_cost} cr)` : 'Enviar'}
      cancelText="Cancelar"
      width={560}
      destroyOnClose
    >
      {/* Card readonly com dados do jogo */}
      {match && (
        <div style={{
          background: '#f0f9f4', borderLeft: `4px solid ${BRAND}`, padding: 14,
          borderRadius: 10, marginBottom: 16,
        }}>
          <Title level={5} style={{ margin: 0, color: '#075E54' }}>{match.title}</Title>
          <div style={{ marginTop: 6, color: '#333', fontSize: 13, lineHeight: 1.6 }}>
            <CalendarOutlined /> {matchDate}{matchTime ? ` · ${matchTime}` : ''}
            {match.quadra && <><br /><EnvironmentOutlined /> {match.quadra}{match.city ? ` · ${match.city}` : ''}</>}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 600 }}>
          <MailOutlined /> Enviar por email pra
        </div>
        <Tag color="green">{selectedIds.length} selecionado(s)</Tag>
      </div>

      {loadingRoster ? (
        <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
      ) : roster.length === 0 ? (
        <Empty description="Nenhum jogador no elenco. Cadastre em /time/elenco antes." />
      ) : availableRoster.length === 0 ? (
        <Alert type="warning" showIcon
          message="Nenhum jogador elegível pra esse canal"
          description={
            channel === 'email'
              ? `Os ${roster.length} jogadores do elenco ainda não têm email. Peça pra cadastrarem em /player/profile.`
              : `Nenhum jogador do elenco está habilitado nesse canal.`
          }
        />
      ) : (
        <>
          <div style={{ marginBottom: 8 }}>
            <Checkbox
              checked={availableRoster.every(p => selectedIds.includes(p.cp_id))}
              indeterminate={
                availableRoster.some(p => selectedIds.includes(p.cp_id)) &&
                !availableRoster.every(p => selectedIds.includes(p.cp_id))
              }
              onChange={e => toggleAll(availableRoster, e.target.checked)}
            >
              <span style={{ fontSize: 12, color: '#666' }}>Selecionar todos ({availableRoster.length})</span>
            </Checkbox>
          </div>
          <div style={{
            maxHeight: 260, overflowY: 'auto', border: '1px solid #f0f0f0',
            borderRadius: 8, padding: 8,
          }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {availableRoster.map(p => (
                <Checkbox
                  key={p.cp_id}
                  checked={selectedIds.includes(p.cp_id)}
                  onChange={e => setSelectedIds(prev =>
                    e.target.checked ? [...prev, p.cp_id] : prev.filter(x => x !== p.cp_id)
                  )}
                >
                  <span style={{ fontWeight: 500 }}>{p.name}</span>
                  {channel === 'email' && (
                    <span style={{ color: '#8c8c8c', fontSize: 11, marginLeft: 8 }}>{p.email}</span>
                  )}
                </Checkbox>
              ))}
            </Space>
          </div>
          {excludedCount > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#8c8c8c' }}>
              {excludedCount} jogador(es) do elenco não aparecem aqui — {excludedReason}.
            </div>
          )}
        </>
      )}

      {selectedIds.length > 0 && (
        <div style={{
          background: '#fafafa', padding: 12, borderRadius: 8, border: '1px solid #f0f0f0',
          marginTop: 12,
        }}>
          {loadingQuote ? (
            <Spin size="small" />
          ) : quote ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <DollarOutlined style={{ color: BRAND }} /> <strong>{quote.credits_cost} crédito(s)</strong>
                <span style={{ color: '#8c8c8c', fontSize: 12, marginLeft: 8 }}>
                  ({quote.n_recipients} emails · 10 emails/crédito)
                </span>
              </div>
              <div style={{ fontSize: 12, color: quote.credits_cost > quote.credits_available ? '#ff4d4f' : '#8c8c8c' }}>
                Saldo: <strong>{quote.credits_available}</strong>
                {quote.credits_cost > quote.credits_available && ' · insuficiente!'}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
